import { useState } from 'react';
import type { PersoonRij } from '@vip/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import { ApiError } from '../../lib/apiClient';
import { RechtenBadge } from './RechtenBadge';
import { useHistorie, usePersonen, useVerwijderDefinitief, useZetTerug } from './personen.api';

/**
 * Wie er niet meer meedoet, met zijn begeleidingsgeschiedenis erbij.
 *
 * Archiveren en niet verwijderen: de historie is juist waar het archief voor
 * bestaat, en `Meeting.buddyId` staat op `restrict`, dus wie gesprekken heeft
 * kán niet weg. Definitief verwijderen kan alleen als er niets aan hangt, en de
 * server weigert de rest met een melding die zegt waarom.
 */
export function ArchiefTab() {
  const { data } = usePersonen();
  const gearchiveerd = (data?.personen ?? []).filter((p) => p.gearchiveerdOp);

  if (gearchiveerd.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>Het archief is leeg.</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Archief
          <Badge variant="secondary">{gearchiveerd.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ItemGroup>
          {gearchiveerd.map((p) => (
            <ArchiefRegel key={p.id} persoon={p} />
          ))}
        </ItemGroup>
      </CardContent>
    </Card>
  );
}

function ArchiefRegel({ persoon }: { persoon: PersoonRij }) {
  const [open, setOpen] = useState(false);
  const [fout, setFout] = useState('');
  const [bevestig, setBevestig] = useState(false);
  const zetTerug = useZetTerug();
  const verwijder = useVerwijderDefinitief();
  const historie = useHistorie(open ? persoon.id : null);

  const gearchiveerd = persoon.gearchiveerdOp
    ? new Date(persoon.gearchiveerdOp).toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <>
      <Collapsible onOpenChange={setOpen} open={open}>
        <Item variant="muted">
          <ItemContent>
            <ItemTitle>
              {persoon.naam}
              <RechtenBadge rechten={persoon.rechten} />
            </ItemTitle>
            <ItemDescription>
              gearchiveerd op {gearchiveerd}
              {persoon.gesprekken > 0
                ? ` · ${persoon.gesprekken} ${persoon.gesprekken === 1 ? 'gesprek' : 'gesprekken'}`
                : ''}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <CollapsibleTrigger asChild>
              <Button size="sm" variant="ghost">
                {open ? 'Historie verbergen' : 'Historie'}
              </Button>
            </CollapsibleTrigger>
            <Button
              disabled={zetTerug.isPending}
              onClick={() => zetTerug.mutate(persoon.id)}
              size="sm"
              variant="ghost"
            >
              Terugzetten
            </Button>
            <Button onClick={() => setBevestig(true)} size="sm" variant="ghost">
              Verwijderen
            </Button>
          </ItemActions>
        </Item>

        <CollapsibleContent>
          <div className="px-4 py-3">
            {historie.data?.koppelingen.length === 0 ? (
              <p className="text-muted-foreground">Nooit aan iemand gekoppeld geweest.</p>
            ) : (
              <ItemGroup>
                {(historie.data?.koppelingen ?? []).map((k) => (
                  <Item key={k.id} size="sm">
                    <ItemContent>
                      <ItemTitle>
                        {k.buddy.naam} begeleidde {k.medewerker.naam}
                      </ItemTitle>
                      <ItemDescription>
                        {new Date(k.startDatum).toLocaleDateString('nl-NL')} tot{' '}
                        {k.eindDatum ? new Date(k.eindDatum).toLocaleDateString('nl-NL') : 'nu'}
                      </ItemDescription>
                    </ItemContent>
                  </Item>
                ))}
              </ItemGroup>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      <AlertDialog onOpenChange={setBevestig} open={bevestig}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{persoon.naam} definitief verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit kan niet ongedaan worden gemaakt. De begeleidingsgeschiedenis verdwijnt mee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setFout('');
                try {
                  await verwijder.mutateAsync(persoon.id);
                } catch (err) {
                  setFout(err instanceof ApiError ? err.message : String(err));
                }
              }}
              variant="destructive"
            >
              Ja, definitief verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
